import { Arrange, Skeleton, useSnackbar } from "@fullscript/aviary-web";
import { captureException } from "@sentry/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { PageFooter } from "@shared/layouts";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import {
  LabsDirectCheckoutEligibilityReason,
  LabsDirectCheckoutItemType,
} from "@shared/types/graphqlGenerated";
import { SwiftFillSteps } from "@shared/types/SwiftFillSteps";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { resolveDirectLabCheckoutSource } from "@shared/utils/resolveDirectLabCheckoutSource/resolveDirectLabCheckoutSource";
import { DirectLabsAuthStartForm } from "@unauthenticated/signupSignin/components/DirectLabsAuthStartForm/DirectLabsAuthStartForm";
import {
  DirectLabsBlockedState,
  type BlockedClass,
} from "@unauthenticated/signupSignin/components/DirectLabsBlockedState/DirectLabsBlockedState";
import { DirectLabsOrderSummary } from "@unauthenticated/signupSignin/components/DirectLabsOrderSummary/DirectLabsOrderSummary";
import { JourneyCheckoutHeaderNav } from "@unauthenticated/signupSignin/components/JourneyCheckoutHeaderNav/JourneyCheckoutHeaderNav";
import { JourneyCheckoutLayout } from "@unauthenticated/signupSignin/components/JourneyCheckoutLayout/JourneyCheckoutLayout";
import { JourneyEmailFormContent } from "@unauthenticated/signupSignin/components/JourneyEmailFormContent/JourneyEmailFormContent";
import { JourneyLoginForm } from "@unauthenticated/signupSignin/components/JourneyLoginForm/JourneyLoginForm";
import { JourneyMobileOrderSummary } from "@unauthenticated/signupSignin/components/JourneyMobileOrderSummary/JourneyMobileOrderSummary";
import { JourneyPatientInfoForm } from "@unauthenticated/signupSignin/components/JourneyPatientInfoForm/JourneyPatientInfoForm";
import { JourneySuccessContent } from "@unauthenticated/signupSignin/components/JourneySuccessContent/JourneySuccessContent";
import { useJourneyWaitlistAddUnauthenticatedSignUpSignInMutation } from "@unauthenticated/signupSignin/components/JourneyWaitlistModal/JourneyWaitlistAdd.mutation.generated";
import JourneyWaitlistModal from "@unauthenticated/signupSignin/components/JourneyWaitlistModal/JourneyWaitlistModal";
import {
  useDirectLabsCheckoutEligibilityUnauthenticatedSignUpSignInLazyQuery,
  useDirectLabsCheckoutEligibilityUnauthenticatedSignUpSignInQuery,
} from "@unauthenticated/signupSignin/data/queries/DirectLabsCheckoutEligibilityQuery/DirectLabsCheckoutEligibility.query.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { usePatientNextLoginStepUnauthenticatedSignUpSignInMutation } from "../../scenes/PatientAuthStartPage/EmailCheckForm/PatientNextLoginStep.mutation.generated";

import * as styles from "./DirectLabsCheckoutPage.styles";

const URL_PARAMS = {
  MAGIC_LINK_PAGE: "magic_link_page",
  MAGIC_LINK_EMAIL: "magic_link_email",
} as const;

const MAGIC_LINK_PAGES = {
  EMAIL: "email",
  SUCCESS: "success",
} as const;

type MagicLinkPage = (typeof MAGIC_LINK_PAGES)[keyof typeof MAGIC_LINK_PAGES];

const isMagicLinkPage = (value: string | null): value is MagicLinkPage =>
  value === MAGIC_LINK_PAGES.EMAIL || value === MAGIC_LINK_PAGES.SUCCESS;

// The eligibility GraphQL enum (e.g. "STATE_RESTRICTED") and the
// direct_lab_checkout.* taxonomy's Reason enum (e.g. "state_restricted",
// BUT-1115) share the same values, differing only in case.
const toAnalyticsReason = (reason: LabsDirectCheckoutEligibilityReason) => reason.toLowerCase();

interface LoginState {
  email: string;
  nextStep: SwiftFillSteps;
}

interface PatientInfoState {
  email: string;
}

interface MagicLinkState {
  email?: string;
  page: MagicLinkPage;
}

interface DirectLabsCheckoutLabItem {
  id: string;
  itemType: "test" | "panel";
  name?: string | null;
  price?: number | null;
  testCount?: number | null;
}

interface Props {
  storeId: string;
  storeSlug: string;
  labItem: DirectLabsCheckoutLabItem;
}

const DirectLabsCheckoutPage = ({ storeId, storeSlug, labItem }: Props) => {
  const { phoneLarge } = useBreakpoints();
  const { criticalSnackbar, successSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const location = useLocation();
  const logAnalyticsEvent = useAnalyticsLogger();

  // Stable per-page-load id so entry_viewed / unavailable_screen_viewed /
  // auth_started / waitlist_cta_clicked can be correlated to the same visit
  // in Mixpanel (mirrors the crypto.randomUUID() session_id pattern used by
  // AddOnsDiscoveryModal). useState (not useRef) so it's safe to read during
  // render.
  const [sessionId] = useState(() => crypto.randomUUID());
  const hasLoggedEntryViewed = useRef(false);
  const hasLoggedUnavailableScreen = useRef(false);

  const basePayload = useMemo(
    () => ({
      store_id: storeId,
      store_slug: storeSlug,
      item_id: labItem.id,
      item_type: labItem.itemType,
      checkout_source: resolveDirectLabCheckoutSource(),
      session_id: sessionId,
    }),
    [storeId, storeSlug, labItem.id, labItem.itemType, sessionId]
  );

  const authenticatedRedirectPath = useMemo(
    () => `/r/checkout/${storeSlug}/labs/${labItem.itemType}/${encodeURIComponent(labItem.id)}`,
    [labItem.id, labItem.itemType, storeSlug]
  );

  // Pre-auth the visitor's state is unknown, so spreeStateId is omitted and the
  // state-restriction check is deferred (re-checked post-auth and at order
  // creation). Blocked verdicts here are structural, or state-restricted once
  // state context flows through (BUT-1110).
  const {
    data: eligibilityData,
    loading: eligibilityLoading,
    error: eligibilityError,
  } = useDirectLabsCheckoutEligibilityUnauthenticatedSignUpSignInQuery({
    variables: {
      storeSlug,
      itemType:
        labItem.itemType === "panel"
          ? LabsDirectCheckoutItemType.Panel
          : LabsDirectCheckoutItemType.Test,
      itemId: labItem.id,
    },
  });

  useEffect(() => {
    if (!eligibilityError) return;

    captureException(eligibilityError, {
      tags: { query: "labsDirectCheckoutEligibility", storeSlug },
    });
  }, [eligibilityError, storeSlug]);

  const verdict = eligibilityData?.viewer?.labsDirectCheckoutEligibility;
  const blockedClass: BlockedClass | null = useMemo(() => {
    if (!verdict || verdict.eligible) return null;

    return verdict.reason === LabsDirectCheckoutEligibilityReason.StateRestricted
      ? "stateRestricted"
      : "structural";
  }, [verdict]);

  // Fire once per page load, independent of the eligibility verdict — this
  // event marks that the entry page itself was viewed (BUT-1116).
  useEffect(() => {
    if (hasLoggedEntryViewed.current) return;

    void logAnalyticsEvent({
      eventName: AnalyticsEvent.DIRECT_LAB_CHECKOUT_ENTRY_VIEWED,
      payload: basePayload,
    });
    hasLoggedEntryViewed.current = true;
  }, [basePayload, logAnalyticsEvent]);

  const getInitialMagicLinkState = (): MagicLinkState | null => {
    const params = new URLSearchParams(location.search);
    const magicLinkPage = params.get(URL_PARAMS.MAGIC_LINK_PAGE);
    const magicLinkEmail = params.get(URL_PARAMS.MAGIC_LINK_EMAIL);

    if (isMagicLinkPage(magicLinkPage)) {
      return {
        page: magicLinkPage,
        email: magicLinkEmail || undefined,
      };
    }

    return null;
  };

  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const currentUserEmailRef = useRef("");
  const [currentZipCode, setCurrentZipCode] = useState("");
  // Entry location gate (BUT-1184): zip -> state -> patient-state restriction,
  // evaluated server-side by the same eligibility service. True means the
  // visitor's zip resolved to a state where this item can't be sold.
  const [isZipGateBlocked, setIsZipGateBlocked] = useState(false);
  const [isZipGateLoading, setIsZipGateLoading] = useState(false);
  const isContinueInFlightRef = useRef(false);

  // Fire once when a blocked state (structural, verdict-driven state
  // restriction, or the zip-derived state-restriction gate) first renders.
  // Deliberately excludes the eligibilityError structural-block screen
  // (captured to Sentry above instead): the taxonomy's `reason` is a closed
  // enum of domain eligibility codes (BUT-1107), and a query failure isn't
  // one of them — sending a fabricated reason would misrepresent the funnel
  // data rather than fill a real gap.
  useEffect(() => {
    if (hasLoggedUnavailableScreen.current) return;
    if (!blockedClass && !isZipGateBlocked) return;

    const reason = blockedClass
      ? toAnalyticsReason(verdict?.reason ?? LabsDirectCheckoutEligibilityReason.StateRestricted)
      : toAnalyticsReason(LabsDirectCheckoutEligibilityReason.StateRestricted);

    void logAnalyticsEvent({
      eventName: AnalyticsEvent.DIRECT_LAB_CHECKOUT_UNAVAILABLE_SCREEN_VIEWED,
      payload: { ...basePayload, reason },
    });
    hasLoggedUnavailableScreen.current = true;
  }, [blockedClass, isZipGateBlocked, verdict, basePayload, logAnalyticsEvent]);

  const [loginState, setLoginState] = useState<LoginState | null>(null);
  const [patientInfoState, setPatientInfoState] = useState<PatientInfoState | null>(null);
  const [magicLinkState, setMagicLinkState] = useState<MagicLinkState | null>(
    getInitialMagicLinkState()
  );
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);

  const updateUrlWithMagicLinkState = (state: MagicLinkState | null) => {
    const params = new URLSearchParams(location.search);

    params.delete(URL_PARAMS.MAGIC_LINK_PAGE);
    params.delete(URL_PARAMS.MAGIC_LINK_EMAIL);

    if (state) {
      params.set(URL_PARAMS.MAGIC_LINK_PAGE, state.page);
      if (state.email) {
        params.set(URL_PARAMS.MAGIC_LINK_EMAIL, state.email);
      }
    }

    const queryString = params.toString();
    const newUrl = `${location.pathname}${queryString ? `?${queryString}` : ""}`;
    window.history.replaceState({}, "", newUrl);
  };

  const updateMagicLinkState = (state: MagicLinkState | null) => {
    setMagicLinkState(state);
    updateUrlWithMagicLinkState(state);
  };

  // Same mutation and join service as the Journey checkout waitlist — the
  // waitlist is keyed on (email, store) and has no Journey-package coupling,
  // so direct-lab items reuse it as-is (BUT-1114). Joining twice is idempotent
  // on the backend (find_or_initialize_by email + store).
  const [addToWaitlist, { loading: addToWaitlistLoading }] =
    useJourneyWaitlistAddUnauthenticatedSignUpSignInMutation({
      onCompleted: data => {
        if (data?.labsJourneysJoinWaitlist?.joined) {
          successSnackbar(t(l.directLabsCheckout.waitlist.successToast));
        } else {
          criticalSnackbar(t(l.directLabsCheckout.waitlist.errorToast));
        }
      },
      onError: error => {
        criticalSnackbar(error?.message || t(l.directLabsCheckout.waitlist.errorToast));
      },
    });

  const [checkPatientNextLoginStep, { loading: userCheckLoading }] =
    usePatientNextLoginStepUnauthenticatedSignUpSignInMutation({
      onCompleted: data => {
        const userCheckResult = data?.auth?.patientNextLoginStep;

        if (userCheckResult?.errors?.message) {
          criticalSnackbar(userCheckResult.errors.message);

          return;
        }

        const nextStep = userCheckResult?.nextStep;
        const submittedEmail = currentUserEmailRef.current;

        const logAuthStarted = (authMode: "signup" | "signin") =>
          void logAnalyticsEvent({
            eventName: AnalyticsEvent.DIRECT_LAB_CHECKOUT_AUTH_STARTED,
            payload: {
              ...basePayload,
              auth_mode: authMode,
              redirect_path: authenticatedRedirectPath,
            },
          });

        switch (nextStep) {
          case SwiftFillSteps.CREATE_ACCOUNT:
            logAuthStarted("signup");
            setPatientInfoState({ email: submittedEmail });
            break;
          case SwiftFillSteps.PASSWORD:
            logAuthStarted("signin");
            setLoginState({ email: submittedEmail, nextStep });
            break;
          case SwiftFillSteps.MAGIC_LINK:
            logAuthStarted("signin");
            updateMagicLinkState({
              email: submittedEmail,
              page: MAGIC_LINK_PAGES.SUCCESS,
            });
            break;
          default:
            criticalSnackbar(t(l.directLabsCheckout.authStart.errors.generalError));
        }
      },
      onError: error => {
        criticalSnackbar(error?.message || t(l.directLabsCheckout.authStart.errors.generalError));
      },
    });

  const [checkZipEligibility] =
    useDirectLabsCheckoutEligibilityUnauthenticatedSignUpSignInLazyQuery();

  // Runs the zip-derived state-restriction gate. Returns true when the flow
  // may proceed: eligible, deferred (unresolvable zip), or a failed check all
  // proceed — the gate is an early advisory signal, never a licence to sell;
  // order-creation and place-order re-checks stay authoritative. Only an
  // explicit STATE_RESTRICTED verdict blocks here. Failures still proceed but
  // are reported so a gate outage is observable (the backend resolver does
  // the same via ReportError).
  const runLocationGate = async (zipCode: string): Promise<boolean> => {
    setIsZipGateLoading(true);

    try {
      const { data, error } = await checkZipEligibility({
        variables: {
          storeSlug,
          itemType:
            labItem.itemType === "panel"
              ? LabsDirectCheckoutItemType.Panel
              : LabsDirectCheckoutItemType.Test,
          itemId: labItem.id,
          zipCode,
        },
        fetchPolicy: "network-only",
      });

      if (error) {
        captureException(error, { tags: { gate: "directLabsLocationGate", storeSlug } });

        return true;
      }

      const zipVerdict = data?.viewer?.labsDirectCheckoutEligibility;
      const isStateRestricted =
        !!zipVerdict &&
        !zipVerdict.eligible &&
        zipVerdict.reason === LabsDirectCheckoutEligibilityReason.StateRestricted;

      if (isStateRestricted) {
        setIsZipGateBlocked(true);

        return false;
      }

      return true;
    } catch (thrown) {
      captureException(thrown, { tags: { gate: "directLabsLocationGate", storeSlug } });

      return true;
    } finally {
      setIsZipGateLoading(false);
    }
  };

  const handleContinue = async (email: string, zipCode: string) => {
    // The Continue button disables via isZipGateLoading, but that state
    // commits asynchronously — the ref closes the double-click window where
    // two eligibility + recaptcha/next-step rounds could fire.
    if (isContinueInFlightRef.current) return;
    isContinueInFlightRef.current = true;

    try {
      setCurrentUserEmail(email);
      currentUserEmailRef.current = email;
      setCurrentZipCode(zipCode);

      const canProceed = await runLocationGate(zipCode);
      if (!canProceed) return;

      gRecaptchaExecute(
        (captchaToken: string) => {
          checkPatientNextLoginStep({
            variables: {
              input: {
                attributes: {
                  email,
                  redirectPath: authenticatedRedirectPath,
                  storeId,
                },
                captchaToken,
              },
            },
          });
        },
        () => {
          criticalSnackbar(t(l.directLabsCheckout.authStart.errors.recaptchaError));
        }
      );
    } finally {
      isContinueInFlightRef.current = false;
    }
  };

  const handleAuthSuccess = () => {
    window.location.assign(authenticatedRedirectPath);
  };

  const handleMagicLinkClick = () => {
    updateMagicLinkState({
      email: loginState?.email || currentUserEmailRef.current,
      page: MAGIC_LINK_PAGES.EMAIL,
    });
  };

  const handleMagicLinkEmailChange = (email: string) => {
    updateMagicLinkState({
      email,
      page: magicLinkState?.page || MAGIC_LINK_PAGES.EMAIL,
    });
  };

  const handleBackToStart = () => {
    setIsZipGateBlocked(false);
    setLoginState(null);
    setPatientInfoState(null);
    updateMagicLinkState(null);
  };

  const handleBackNavigation = () => {
    // The zip-gate block is in-page state like the auth sub-screens: back
    // must return to the entry form (e.g. to fix a mistyped zip), not eject
    // the visitor out of checkout. Waitlist CTA lands with BUT-1114.
    if (isZipGateBlocked || loginState || patientInfoState || magicLinkState) {
      handleBackToStart();

      return;
    }

    window.history.back();
  };

  const handleWaitlistSubmit = (email: string, zipcode?: string) => {
    setCurrentUserEmail(email);
    currentUserEmailRef.current = email;

    addToWaitlist({
      variables: {
        input: {
          attributes: {
            email,
            zipcode: zipcode ?? "",
            storeSlug,
          },
        },
      },
    });
  };

  const { name: itemName, price: itemPrice } = labItem;
  // A structurally unavailable item must not present purchase details; a
  // state-restricted item is a real catalog item the visitor simply can't buy
  // here, so its summary stays visible alongside the restricted message.
  const hasItemDetails =
    itemName !== null &&
    itemName !== undefined &&
    itemPrice !== null &&
    itemPrice !== undefined &&
    !eligibilityError &&
    blockedClass !== "structural";

  const renderOrderSummary = () => {
    if (!hasItemDetails || itemName === null || itemName === undefined) return null;
    if (itemPrice === null || itemPrice === undefined) return null;

    return (
      <DirectLabsOrderSummary
        itemType={labItem.itemType}
        name={itemName}
        price={itemPrice}
        testCount={labItem.testCount ?? 1}
      />
    );
  };

  const renderStartForm = () => (
    <DirectLabsAuthStartForm
      initialEmail={currentUserEmail}
      initialZipCode={currentZipCode}
      itemName={itemName}
      loading={userCheckLoading || isZipGateLoading}
      onContinue={handleContinue}
    />
  );

  const renderLoginForm = () => (
    <JourneyLoginForm
      onLoginSuccess={handleAuthSuccess}
      onMagicLinkClick={handleMagicLinkClick}
      email={loginState?.email}
      storeSlug={storeSlug}
      storeId={storeId}
      authenticatedRedirectPath={authenticatedRedirectPath}
      loginState={loginState || undefined}
    />
  );

  const renderPatientInfoForm = () => (
    <JourneyPatientInfoForm
      onPatientCreated={handleAuthSuccess}
      email={patientInfoState?.email}
      storeId={storeId}
      authenticatedRedirectPath={authenticatedRedirectPath}
      source="labs_self_checkout"
      patientInfoState={patientInfoState || undefined}
    />
  );

  const renderMagicLinkEmailForm = () => (
    <JourneyEmailFormContent
      email={magicLinkState?.email || ""}
      storeId={storeId}
      setEmail={handleMagicLinkEmailChange}
      setPage={page => {
        if (page === MAGIC_LINK_PAGES.SUCCESS) {
          updateMagicLinkState({
            email: magicLinkState?.email,
            page: MAGIC_LINK_PAGES.SUCCESS,
          });
        }
      }}
      onBackToLogin={handleBackToStart}
      authenticatedRedirectPath={authenticatedRedirectPath}
    />
  );

  const renderMagicLinkSuccess = () => (
    <JourneySuccessContent
      email={magicLinkState?.email || ""}
      setPage={page => {
        if (page === MAGIC_LINK_PAGES.EMAIL) {
          updateMagicLinkState({
            email: magicLinkState?.email,
            page: MAGIC_LINK_PAGES.EMAIL,
          });
        }
      }}
      isSwiftFillSignIn={false}
      onBackToLogin={handleBackToStart}
    />
  );

  const renderLoadingSkeleton = () => (
    <div role="status" aria-busy="true" aria-label={t(l.directLabsCheckout.loading)}>
      <Arrange isVertical gap="large" isFullWidth>
        <Skeleton width="100%" height={32} />
        <Skeleton width="100%" height={64} />
        <Skeleton width="100%" height={48} />
      </Arrange>
    </div>
  );

  // Fires on the CTA click itself (opening the modal), not on submit — the
  // click is the funnel action; submission is a separate step the taxonomy
  // doesn't track yet (BUT-1116).
  const handleJoinWaitlistClick = (reason: LabsDirectCheckoutEligibilityReason) => {
    void logAnalyticsEvent({
      eventName: AnalyticsEvent.DIRECT_LAB_CHECKOUT_WAITLIST_CTA_CLICKED,
      payload: { ...basePayload, reason: toAnalyticsReason(reason) },
    });
    setIsWaitlistModalOpen(true);
  };

  const renderLeftContent = () => {
    // Show a skeleton (not a blank column) while the eligibility verdict
    // resolves, so blocked visitors never flash the sell flow.
    if (eligibilityLoading) {
      return renderLoadingSkeleton();
    }

    if (eligibilityError) {
      return <DirectLabsBlockedState blockedClass="structural" />;
    }

    if (blockedClass) {
      return (
        <DirectLabsBlockedState
          blockedClass={blockedClass}
          onJoinWaitlist={
            blockedClass === "stateRestricted"
              ? () =>
                  handleJoinWaitlistClick(
                    verdict?.reason ?? LabsDirectCheckoutEligibilityReason.StateRestricted
                  )
              : undefined
          }
        />
      );
    }

    // Zip-derived state restriction (entry location gate, BUT-1184). Same
    // blocked screen as an API-reported state restriction, and — since the zip
    // gate is the reachable pre-auth state-restricted path — this is where the
    // waitlist CTA actually surfaces (BUT-1114).
    if (isZipGateBlocked) {
      return (
        <DirectLabsBlockedState
          blockedClass="stateRestricted"
          onJoinWaitlist={() =>
            handleJoinWaitlistClick(LabsDirectCheckoutEligibilityReason.StateRestricted)
          }
        />
      );
    }

    if (magicLinkState?.page === MAGIC_LINK_PAGES.EMAIL) {
      return renderMagicLinkEmailForm();
    }

    if (magicLinkState?.page === MAGIC_LINK_PAGES.SUCCESS) {
      return renderMagicLinkSuccess();
    }

    if (patientInfoState) {
      return renderPatientInfoForm();
    }

    if (loginState) {
      return renderLoginForm();
    }

    return renderStartForm();
  };

  const isMobile = !phoneLarge.greaterThan;

  return (
    <div css={styles.pageContainer}>
      <JourneyCheckoutHeaderNav onBack={handleBackNavigation} />
      <div css={styles.contentArea}>
        {isMobile ? (
          <div css={styles.mobileLayout}>
            {!!hasItemDetails && (
              <JourneyMobileOrderSummary orderTotal={itemPrice ?? 0}>
                {renderOrderSummary()}
              </JourneyMobileOrderSummary>
            )}
            {renderLeftContent()}
          </div>
        ) : (
          <JourneyCheckoutLayout
            leftContent={renderLeftContent()}
            rightContent={renderOrderSummary()}
          />
        )}
      </div>
      <PageFooter type="checkout" hideShippingDisclaimer />
      <JourneyWaitlistModal
        isOpen={isWaitlistModalOpen}
        onClose={() => setIsWaitlistModalOpen(false)}
        prefilledEmail={currentUserEmail}
        isLoading={addToWaitlistLoading}
        onSubmit={handleWaitlistSubmit}
        waitlistReason={null}
        content={{
          title: t(l.directLabsCheckout.waitlist.title),
          description: t(l.directLabsCheckout.waitlist.description),
        }}
        showZipCodeInput
      />
    </div>
  );
};

export { type DirectLabsCheckoutLabItem, DirectLabsCheckoutPage };
